import { useState } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import env from 'react-dotenv';
import Logo from 'assets/icons/finefinders-logo.svg';
import EyeIcon from 'assets/icons/eye.svg';
import GreenEyeIcon from 'assets/icons/eye-green.svg';
import { PAGE_PATHS } from '../../constants';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { isLoggedInSelector } from '../../store/selectors';

function SetupUser() {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [clientId, setClientId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showPasswordOne, setShowPasswordOne] = useState(false);
  const [showPasswordTwo, setShowPasswordTwo] = useState(false);
  const isResetPassword = location.pathname === PAGE_PATHS.RESET_PASSWORD;

  const handleSubmit = async () => {
    if (step === 0) {
      try {
        toast.loading(t('setup.user.toast.loading.message'));
        await axios.post(`${env.REACT_APP_API_URL}/check-user`, {
          Client_ID: clientId,
          email,
        });
        toast.dismiss();
        setStep(1);
      } catch (err) {
        toast.dismiss();
        toast.error(err.response?.data?.error);
      }
    }

    if (step === 1) {
      if (password !== confirmPassword) {
        setError('err');
        return;
      }

      const setupUser = axios.post(
        `${env.REACT_APP_API_URL}/setup-user`,
        { Client_ID: clientId, email, password, confirmPassword },
      );
      const callback = {
        loading: t('setup.user.toast.loading.message.2'),
        success: value => {
          navigate(PAGE_PATHS.ROOT);
          setStep(2);
          return t('setup.user.toast.success.message');
        },
        error: err => err.response?.data?.error,
      };
      toast.promise(setupUser, callback);
    }
  };

  if (isLoggedIn) {
    return <Navigate to={PAGE_PATHS.ROOT} />;
  }

  return (
    <div className="setup-user">
      <div className="side" />
      <div className="container">
        <div className="content-container">
          <img alt="Logo" src={Logo} />

          {step === 0 && (
            <>
              <p>
                {isResetPassword
                  ? t('setup.user.input.password.and.id')
                  : t('setup.user.register.with.email.and.id')}
              </p>
              <div className="input-container">
                <label>{t('setup.user.email.label')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
              <div className="input-container">
                <label>{t('setup.user.client.number.label')}</label>
                <input
                  type="text"
                  value={clientId}
                  onChange={e => setClientId(e.target.value)}
                />
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <p>{t('setup.user.create.password.text')}</p>
              <div className="input-container">
                <label>{t('setup.user.password.label')}</label>
                <input
                  type={showPasswordOne ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  className="view-password"
                  onClick={() => setShowPasswordOne(v => !v)}>
                  <img
                    alt="Eye Icon"
                    src={showPasswordOne ? GreenEyeIcon : EyeIcon}
                  />
                </button>
              </div>
              <div className="input-container">
                <label>{t('setup.user.confirm.password.label')}</label>
                <input
                  type={showPasswordTwo ? 'text' : 'password'}
                  className={error ? 'error' : ''}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  onFocus={() => setError('')}
                />
                <button
                  className="view-password"
                  onClick={() => setShowPasswordTwo(v => !v)}>
                  <img
                    alt="Eye Icon"
                    src={showPasswordTwo ? GreenEyeIcon : EyeIcon}
                  />
                </button>
                {error && (
                  <p className="error">
                    {t('setup.user.password.does.not.match')}
                  </p>
                )}
              </div>
            </>
          )}

          <div className="button-container">
            <p className="note">
              {t('setup.user.manager.text')}

              <a
                target="_blank"
                style={{ display: 'block' }}
                rel="noreferrer"
                href="https://www.finefindersonline.com/free-consultation">
                {t('setup.user.manager.link')}
              </a>
            </p>
            <button className="button" onClick={handleSubmit}>
              {step === 0
                ? t('setup.user.next.button')
                : t('setup.user.register.button')}
            </button>
          </div>

          <p className="bottom note">
            {t('setup.user.already.have.an.account')}
            <Link to={PAGE_PATHS.LOGIN}>{t('setup.user.login.link')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SetupUser;
